/**
 * Shared client-side image optimizer — every user upload (studio backgrounds,
 * community banners, avatars) funnels through here before hitting storage.
 * Downscales to a per-kind cap and re-encodes to WebP so multi-MB camera shots
 * never reach the database or a bucket. Data URLs in rows are banned; this is
 * the pipeline that makes that ban cheap to live with.
 */

export type ImageKind = 'background' | 'banner' | 'avatar';

/** Long-edge pixel caps per asset kind — big enough to stay cinematic, small enough to stream. */
const EDGE_CAPS: Record<ImageKind, number> = {
  background: 2560,
  banner: 1600,
  avatar: 512,
};

const WEBP_QUALITY = 0.82;
const MAX_INPUT_BYTES = 10 * 1024 * 1024;

export interface OptimizedImage {
  blob: Blob;
  width: number;
  height: number;
  bytes: number;
  /** SHA-256 of the encoded output — lets the backend dedupe identical uploads. */
  contentHash: string;
  ext: 'webp';
}

async function decode(file: Blob): Promise<ImageBitmap | HTMLImageElement> {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file);
    } catch {
      /* fall through to <img> decode (e.g. unsupported source) */
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function toBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('Image encode failed'))),
      'image/webp',
      WEBP_QUALITY,
    );
  });
}

async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * Downscale + re-encode an image for upload. Rejects absurd inputs instead of
 * silently uploading them; keeps aspect ratio; always outputs WebP.
 */
export async function optimizeImage(file: File | Blob, kind: ImageKind): Promise<OptimizedImage> {
  if (file.size > MAX_INPUT_BYTES) {
    throw new Error('Image is too large (max 10 MB) — try a smaller file.');
  }
  const source = await decode(file);
  const srcW = source.width;
  const srcH = source.height;
  if (!srcW || !srcH) throw new Error('Could not read that image.');

  const cap = EDGE_CAPS[kind];
  const scale = Math.min(1, cap / Math.max(srcW, srcH));
  const width = Math.max(1, Math.round(srcW * scale));
  const height = Math.max(1, Math.round(srcH * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable in this browser.');
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, 0, 0, width, height);
  if ('close' in source) source.close();

  const blob = await toBlob(canvas);
  return { blob, width, height, bytes: blob.size, contentHash: await sha256Hex(blob), ext: 'webp' };
}
